import {
  createInitialGridState,
  dirDelta,
  DIRECTIONS,
  INITIAL_UNLOCKED_COUNT,
  type Direction,
  type GridPosition,
  type ModuleGridLane,
  type ModuleGridState,
  type PlacedModule,
} from "../models/ModuleGrid.js";
import { BigNumber } from "@yjsgames/bignumber";
import { MODULE_GRID } from "../data/balance.js";
import { footprintCells } from "../data/moduleFootprints.js";
import { cellPortsForPlaced } from "../data/modulePorts.js";

/**
 * 칸 해금 비용 순수 공식 — base × multiplier^purchasedCount.
 * 현재 데이터는 multiplier=1이라 모든 칸이 같은 다이아 비용이다.
 * BigNumber로 깊은 해금(number 정밀도 9e15 초과)에서도 정확. base/mult/n 주입형이라 단위테스트 대상.
 */
export function computeUnlockCostBig(
  base: number,
  multiplier: number,
  purchasedCount: number,
): BigNumber {
  return BigNumber.fromNumber(base).mul(BigNumber.fromNumber(multiplier).pow(purchasedCount));
}

function cloneState(s: ModuleGridState): ModuleGridState {
  const lanes = (s.lanes && s.lanes.length > 0 ? s.lanes : [{ start: s.start, fire: s.fire }]).map((lane) => ({
    start: { ...lane.start },
    fire: { ...lane.fire },
  }));
  return {
    width: s.width,
    height: s.height,
    start: { ...lanes[0]!.start },
    fire: { ...lanes[0]!.fire },
    lanes,
    blockedCells: (s.blockedCells ?? []).map((c) => ({ ...c })),
    baseUnlockedCount: s.baseUnlockedCount ?? INITIAL_UNLOCKED_COUNT,
    unlockedCells: s.unlockedCells.map((c) => ({ ...c })),
    placedModules: s.placedModules.map((m) => ({ ...m })),
  };
}

function endpointPorts(pos: GridPosition, width: number, height: number, start: boolean): Direction[] {
  if (pos.x < 0) return ["right"];
  if (pos.x >= width) return ["left"];
  if (pos.y < 0) return ["down"];
  if (pos.y >= height) return ["up"];
  return start ? ["right"] : ["left"];
}

/**
 * 모듈 조립 그리드 상태 관리 — 순수 로직(테스트 대상, 지시문 §7·§9). Phaser 비의존.
 * 해금/배치/이동/제거 + 포트 조회. 다이아 차감은 GridCommands가 unlockCost로 수행.
 * (회전·모양 순환 폐기 — 조각(shapeId+footprintId)은 획득 시 고정. 배치는 인스턴스 조각 그대로 사용.)
 */
export class ModuleGridController {
  private readonly state: ModuleGridState;
  private revisionCounter = 0;

  constructor(state?: ModuleGridState) {
    this.state = state ? cloneState(state) : createInitialGridState();
  }

  get width(): number {
    return this.state.width;
  }
  get height(): number {
    return this.state.height;
  }
  get start(): GridPosition {
    return this.state.start;
  }
  get fire(): GridPosition {
    return this.state.fire;
  }
  get lanes(): readonly ModuleGridLane[] {
    return this.state.lanes && this.state.lanes.length > 0
      ? this.state.lanes.map((lane) => ({ start: { ...lane.start }, fire: { ...lane.fire } }))
      : [{ start: { ...this.state.start }, fire: { ...this.state.fire } }];
  }
  get revision(): number {
    return this.revisionCounter;
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.state.width && y < this.state.height;
  }

  isBlocked(x: number, y: number): boolean {
    return (this.state.blockedCells ?? []).some((c) => c.x === x && c.y === y);
  }

  isStart(x: number, y: number): boolean {
    return this.lanes.some((lane) => x === lane.start.x && y === lane.start.y);
  }
  isFire(x: number, y: number): boolean {
    return this.lanes.some((lane) => x === lane.fire.x && y === lane.fire.y);
  }

  /** Start/Fire는 항상 해금 (지시문 §7). */
  isUnlocked(x: number, y: number): boolean {
    if (this.isBlocked(x, y)) return false;
    if (this.isStart(x, y) || this.isFire(x, y)) return true;
    return this.state.unlockedCells.some((c) => c.x === x && c.y === y);
  }

  /**
   * BFS 경로 전파 노드인지 — Start/Fire(범위 외 endpoint) + 배치 모듈만 graph node다.
   * 빈칸/잠금칸은 전파하지 않는다. 모듈은 반드시 Start 또는 다른 모듈과 포트로 맞닿아야 발동한다.
   */
  isPathNode(x: number, y: number): boolean {
    return (
      this.isStart(x, y) ||
      this.isFire(x, y) ||
      this.moduleCoveringCell(x, y) !== null
    );
  }

  /**
   * 구 규칙 호환 조회. 새 전투 규칙에서는 빈칸이 기본 파이프가 아니므로 항상 false다.
   */
  isDefaultConduitCell(x: number, y: number): boolean {
    void x;
    void y;
    return false;
  }

  /** anchor 전용 조회 — (x,y)가 모듈의 anchor일 때만 반환 (렌더 루프에서 anchor 1회 렌더용). */
  getPlacedAt(x: number, y: number): PlacedModule | null {
    return this.state.placedModules.find((m) => m.x === x && m.y === y) ?? null;
  }

  /**
   * 멤버십 조회 — 임의 셀 (x,y)를 footprint로 점유하는 모듈(+해당 셀 index). anchor 아니어도 됨.
   * 충돌/제거/모양순환/포트(라우팅)는 이 메서드 사용. (anchor만 필요한 렌더는 getPlacedAt).
   */
  moduleCoveringCell(x: number, y: number): { tile: PlacedModule; cellIndex: number } | null {
    for (const tile of this.state.placedModules) {
      const cells = footprintCells(tile.footprintId);
      for (let i = 0; i < cells.length; i++) {
        const c = cells[i]!;
        if (tile.x + c.dx === x && tile.y + c.dy === y) return { tile, cellIndex: i };
      }
    }
    return null;
  }

  /** 모듈을 둘 수 있는 칸 (해금 + Start/Fire 아님). 점유 칸은 교체 허용. */
  isPlaceableCell(x: number, y: number): boolean {
    return this.inBounds(x, y) && !this.isBlocked(x, y) && this.isUnlocked(x, y) && !this.isStart(x, y) && !this.isFire(x, y);
  }

  // ── 해금 ──────────────────────────────────────────────────────────────
  private purchasedCount(): number {
    return Math.max(0, this.state.unlockedCells.length - (this.state.baseUnlockedCount ?? INITIAL_UNLOCKED_COUNT));
  }

  /**
   * 다음 칸 해금 비용(BigNumber, SSoT) = base × multiplier^(구매 해금칸수).
   * global.tsv에서 multiplier=1이면 모든 칸이 균일 비용이다.
   * 차감(GridCommands)·표시(formatBig)는 이 값을 사용. 깊은 해금에서 number 정밀도 한계를 넘어 BigNumber가 정답.
   */
  unlockCostBig(): BigNumber {
    return computeUnlockCostBig(
      MODULE_GRID.unlockBaseCost,
      MODULE_GRID.unlockCostMultiplier,
      this.purchasedCount(),
    );
  }

  /** 다음 칸 해금 비용(number) — AutoPlay snapshot 호환용. 매우 깊은 해금에선 근사(정확값은 unlockCostBig). */
  unlockCost(): number {
    return this.unlockCostBig().toNumber();
  }

  /** 인접(상하좌우)한 해금 칸이 있는 잠금 칸만 해금 가능 (지시문 §6 방식 B). */
  canUnlock(x: number, y: number): boolean {
    if (!this.inBounds(x, y) || this.isBlocked(x, y) || this.isUnlocked(x, y)) return false;
    return DIRECTIONS.some((d) => {
      const { dx, dy } = dirDelta(d);
      return this.inBounds(x + dx, y + dy) && this.isUnlocked(x + dx, y + dy);
    });
  }

  unlock(x: number, y: number): boolean {
    if (!this.canUnlock(x, y)) return false;
    this.state.unlockedCells.push({ x, y });
    this.revisionCounter += 1;
    return true;
  }

  /**
   * 모든 잠긴 칸을 비용·인접 검사 없이 강제 해금 (DEV 치트 전용 — CheatActions.fillGridRandomly).
   * 이미 해금된 칸은 중복 추가하지 않으며, Start/Fire는 범위 밖 endpoint라 루프에 포함되지 않는다.
   */
  unlockAll(): void {
    let changed = false;
    for (let y = 0; y < this.state.height; y++) {
      for (let x = 0; x < this.state.width; x++) {
        if (!this.isBlocked(x, y) && !this.isUnlocked(x, y)) {
          this.state.unlockedCells.push({ x, y });
          changed = true;
        }
      }
    }
    if (changed) this.revisionCounter += 1;
  }

  // ── 배치/이동/제거/모양순환 ─────────────────────────────────────────────
  /**
   * footprint 전체를 배치 가능한지 — 점유 셀이 모두 배치 가능 칸 + 다른 인스턴스 미점유.
   * selfId 지정 시 그 인스턴스 자신이 점유한 셀은 충돌로 보지 않음(이동 시 자기 자리 허용).
   */
  canPlace(x: number, y: number, footprintId = "single", selfId?: string): boolean {
    const cells = footprintCells(footprintId);
    for (const c of cells) {
      const cx = x + c.dx;
      const cy = y + c.dy;
      if (!this.isPlaceableCell(cx, cy)) return false;
      const cover = this.moduleCoveringCell(cx, cy);
      if (cover && cover.tile.instanceId !== selfId) return false;
    }
    return true;
  }

  /**
   * 모듈 인스턴스 배치 (anchor=(x,y)). 같은 instanceId의 기존 타일은 회수 후 배치.
   * footprint 점유 셀이 다른 모듈과 겹치거나 배치 불가 칸이면 실패(교체 없음 — 스냅백).
   */
  place(
    x: number,
    y: number,
    instanceId: string,
    definitionId: string,
    shapeId: string,
    footprintId = "single",
  ): boolean {
    if (!this.canPlace(x, y, footprintId, instanceId)) return false;
    this.removeByInstanceId(instanceId);
    this.state.placedModules.push({ x, y, instanceId, definitionId, shapeId, footprintId });
    this.revisionCounter += 1;
    return true;
  }

  /** (x,y)를 점유한 모듈을 통째 제거 (anchor 아니어도 됨). */
  removeAt(x: number, y: number): PlacedModule | null {
    const cover = this.moduleCoveringCell(x, y);
    if (!cover) return null;
    this.removeByInstanceId(cover.tile.instanceId);
    this.revisionCounter += 1;
    return cover.tile;
  }

  private removeByInstanceId(instanceId: string): void {
    this.state.placedModules = this.state.placedModules.filter((m) => m.instanceId !== instanceId);
  }

  /** 배치된 모듈 전부 제거 (해금 상태는 유지) — 환생 시 보유 모듈 초기화에 사용. */
  clearPlacedModules(): void {
    if (this.state.placedModules.length === 0) return;
    this.state.placedModules = [];
    this.revisionCounter += 1;
  }

  /**
   * 배치 타일을 다른 anchor로 이동 (효과/모양/footprint 유지). 대상 footprint가 겹치면 실패.
   * (fromX,fromY)는 모듈의 임의 점유 셀이어도 됨 — 멤버십으로 모듈을 찾는다.
   */
  move(fromX: number, fromY: number, toX: number, toY: number): boolean {
    const cover = this.moduleCoveringCell(fromX, fromY);
    if (!cover) return false;
    const tile = cover.tile;
    if (!this.canPlace(toX, toY, tile.footprintId, tile.instanceId)) return false;
    this.removeByInstanceId(tile.instanceId);
    this.state.placedModules.push({ ...tile, x: toX, y: toY });
    this.revisionCounter += 1;
    return true;
  }

  /**
   * 셀의 현재 포트. 배치 모듈은 cellPortsForPlaced(셀별 모양 합성), 해금 빈칸은 4방향 통로.
   * 잠금/사용불가 칸은 포트 없음([]) → 경로 차단.
   * ⚠ 렌더/UI 표시 전용 — BFS 경로 전파는 pathPortsAt을 사용한다(endpoint/배치 모듈만 전파, 빈칸 전파 X).
   */
  portsAt(x: number, y: number): Direction[] {
    const cover = this.moduleCoveringCell(x, y);
    if (cover) return this.tilePorts(cover.tile, cover.cellIndex);
    if (this.isUnlocked(x, y)) return [...DIRECTIONS];
    return [];
  }

  /**
   * BFS 경로 전용 포트 조회. Start=["right"]·Fire=["left"](단방향 endpoint) / 배치 모듈=shape·footprint 포트 /
   * 빈칸·잠금칸=[](graph node 아님). 모듈과 떨어진 모듈은 빈칸 너머로 발동하지 않는다.
   */
  pathPortsAt(x: number, y: number): Direction[] {
    const ports = new Set<Direction>();
    for (const lane of this.lanes) {
      if (x === lane.start.x && y === lane.start.y) {
        for (const p of endpointPorts(lane.start, this.state.width, this.state.height, true)) ports.add(p);
      }
      if (x === lane.fire.x && y === lane.fire.y) {
        for (const p of endpointPorts(lane.fire, this.state.width, this.state.height, false)) ports.add(p);
      }
    }
    if (ports.size > 0) return [...ports];
    const cover = this.moduleCoveringCell(x, y);
    if (cover) return this.tilePorts(cover.tile, cover.cellIndex);
    return []; // 그 외 빈칸/잠금칸 = graph node 아님
  }

  /** 배치 모듈의 특정 셀 포트 — cellPortsForPlaced(셀별 모양 합성)에 위임. cellIndex는 footprintCells 순서. */
  private tilePorts(tile: PlacedModule, cellIndex: number): Direction[] {
    return cellPortsForPlaced(tile.shapeId, tile.footprintId)[cellIndex] ?? [];
  }

  /** 그리드에 배치된 인스턴스 id 목록. */
  placedInstanceIds(): string[] {
    return this.state.placedModules.map((m) => m.instanceId);
  }

  /** 배치된 모듈 사본 목록 (AutoPlay dump 등). */
  placed(): PlacedModule[] {
    return this.state.placedModules.map((m) => ({ ...m }));
  }

  /** instanceId의 현재 배치(anchor x,y + footprint). 미배치면 undefined. 발동 연출 셀 좌표 조회용. */
  placementOf(instanceId: string): PlacedModule | undefined {
    return this.state.placedModules.find((m) => m.instanceId === instanceId);
  }

  toState(): ModuleGridState {
    return cloneState(this.state);
  }

  resetToState(state: ModuleGridState): void {
    const next = cloneState(state);
    this.state.width = next.width;
    this.state.height = next.height;
    this.state.start = next.start;
    this.state.fire = next.fire;
    this.state.lanes = next.lanes;
    this.state.blockedCells = next.blockedCells;
    this.state.baseUnlockedCount = next.baseUnlockedCount;
    this.state.unlockedCells = next.unlockedCells;
    this.state.placedModules = [];
    this.revisionCounter += 1;
  }

  static fromState(state: ModuleGridState): ModuleGridController {
    return new ModuleGridController(state);
  }
}
